import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RateManagement = () => {
  const { token } = useAuth();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    service_type: '',
    price_per_unit: '',
    unit: 'piece'
  });

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(response.data);
    } catch (error) {
      toast.error('Failed to fetch rates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRate) {
        await axios.put(
          `${API}/rates/${editingRate.id}`,
          { ...formData, price_per_unit: parseFloat(formData.price_per_unit) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rate updated successfully');
      } else {
        await axios.post(
          `${API}/rates`,
          { ...formData, price_per_unit: parseFloat(formData.price_per_unit) },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Rate created successfully');
      }
      setDialogOpen(false);
      setFormData({ item_name: '', service_type: '', price_per_unit: '', unit: 'piece' });
      setEditingRate(null);
      fetchRates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      item_name: rate.item_name,
      service_type: rate.service_type,
      price_per_unit: rate.price_per_unit.toString(),
      unit: rate.unit
    });
    setDialogOpen(true);
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this rate?')) return;
    try {
      await axios.delete(`${API}/rates/${rateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rate deleted successfully');
      fetchRates();
    } catch (error) {
      toast.error('Failed to delete rate');
    }
  };

  const openAddDialog = () => {
    setEditingRate(null);
    setFormData({ item_name: '', service_type: '', price_per_unit: '', unit: 'piece' });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-600">Manage pricing for different items and services</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={openAddDialog}
              data-testid="add-rate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Rate
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingRate ? 'Edit Rate' : 'Add New Rate'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="item-name">Item Name *</Label>
                <Input
                  id="item-name"
                  placeholder="e.g., Shirt, Pants, Dress"
                  value={formData.item_name}
                  onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                  required
                  data-testid="rate-item-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="service-type">Service Type *</Label>
                <Select value={formData.service_type} onValueChange={(value) => setFormData({ ...formData, service_type: value })}>
                  <SelectTrigger data-testid="rate-service-type-select">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wash">Wash</SelectItem>
                    <SelectItem value="dry-clean">Dry Clean</SelectItem>
                    <SelectItem value="iron">Iron</SelectItem>
                    <SelectItem value="wash-iron">Wash + Iron</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Must match service types in Create Ticket</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unit">Unit *</Label>
                <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value, price_per_unit: '' })}>
                  <SelectTrigger data-testid="rate-unit-select">
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="piece">Piece (Integer)</SelectItem>
                    <SelectItem value="kg">KG (Decimal)</SelectItem>
                    <SelectItem value="item">Item</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price per Unit *</Label>
                <Input
                  id="price"
                  type="number"
                  step={formData.unit === 'piece' ? '1' : '0.01'}
                  placeholder={formData.unit === 'piece' ? '0' : '0.00'}
                  value={formData.price_per_unit}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (formData.unit === 'piece' && value.includes('.')) {
                      value = Math.floor(parseFloat(value)).toString();
                    }
                    setFormData({ ...formData, price_per_unit: value });
                  }}
                  required
                  data-testid="rate-price-input"
                />
                <p className="text-xs text-slate-500">
                  {formData.unit === 'piece' ? 'Integer values only for pieces' : 'Decimal values allowed'}
                </p>
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="save-rate-btn">
                {editingRate ? 'Update' : 'Create'} Rate
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rates List */}
      {rates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-slate-400 mb-4">
              <Tag className="w-20 h-20" />
            </div>
            <p className="text-slate-500 text-lg mb-2">No rates configured</p>
            <p className="text-slate-400 text-sm">Add your first rate to streamline ticket creation</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Item Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-600">Service Type</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-600">Price</th>
                    <th className="text-center p-4 text-sm font-semibold text-slate-600">Unit</th>
                    <th className="text-right p-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((rate, index) => (
                    <tr
                      key={rate.id}
                      className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                        index === rates.length - 1 ? 'border-b-0' : ''
                      }`}
                      data-testid={`rate-row-${rate.id}`}
                    >
                      <td className="p-4 text-sm font-medium text-slate-900">{rate.item_name}</td>
                      <td className="p-4 text-sm text-slate-600">{rate.service_type}</td>
                      <td className="p-4 text-sm font-semibold text-right text-indigo-600">
                        ₹{rate.price_per_unit.toFixed(2)}
                      </td>
                      <td className="p-4 text-sm text-center text-slate-600">{rate.unit}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(rate)}
                            data-testid={`edit-rate-${rate.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(rate.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-rate-${rate.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RateManagement;
